import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import ImageUploading from "react-images-uploading";
import BackArrow from "../assets/images/back-arrow.svg";
import Clock from "../assets/icons/clock1.svg";
import NoImage from "../assets/images/no-image.svg";
import {
  allergy_ai_api_key,
  allergy_ai_api_secret_key,
} from "../utils/api-url-list";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";
import imageCompression from "browser-image-compression";

const NewAssessment = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const userLocation = routerLocation.state?.location;
  const [images, setImages] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [imageSeleted, setImageSeleceted] = useState(true);
  const [imageAnalysis, setImageAnalysis] = useState({});
  const [photoAccepted, setPhotoAccepted] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [assesmentId, setAssesmentId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFaceDetected, setFaceDetected] = useState(false);

  const maxNumber = 1;
  const onChange = (imageList) => {
    setImages(imageList);
    setImageSeleceted(false);
  };

  const getConfidence = async () => {
    if (!images.length || !images[0]?.data_url) return;

    setUploadError("");
    setLoading(true);
    try {
      const imageFile = images[0].file;
      const options = {
        maxSizeMB: 5,
        useWebWorker: true,
        preserveExif: false,
        fileType: "image/jpeg",
        initialQuality: 0.8,
      };
      const compressedFile = await imageCompression(imageFile, options);
      const file = new File([compressedFile], `face-${Date.now()}.jpeg`, {
        type: "image/jpeg",
      });

      //  Request presigned upload URL
      const uploadDocResp = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/upload-image",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: allergy_ai_api_secret_key,
          },
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            file_name: "face.jpeg",
            action: "upload",
          }),
        }
      );

      const uploadDocRespJSON = await uploadDocResp.json();

      if (uploadDocRespJSON?.statusCode?.toString().startsWith("2")) {
        setAssesmentId(uploadDocRespJSON?.allergy_assessment_id);
      }

      if (
        !uploadDocResp.ok ||
        !uploadDocRespJSON?.statusCode?.toString().startsWith("2")
      ) {
        throw new Error("Failed to get upload credentials.");
      }

      const { presigned_post, allergy_assessment_id } = uploadDocRespJSON;

      // Upload file to S3
      const FileFormdata = new FormData();
      Object.entries(presigned_post.fields).forEach(([key, value]) => {
        FileFormdata.append(key, value);
      });
      FileFormdata.append("file", file);

      const s3Resp = await fetch(presigned_post.url, {
        method: "POST",
        body: FileFormdata,
      });

      if (!s3Resp.ok) throw new Error("Upload to S3 failed.");

      await new Promise((res) => setTimeout(res, 800));

      //face_image_analysis API
      const imageUrl = presigned_post.url + presigned_post.fields.key;
      setImageUrl(imageUrl);

      const analysisResp = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/allergy-assessment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: allergy_ai_api_secret_key,
          },
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            action: "face_image_analysis",
            allergy_assessment_id,
            location: userLocation,
            file_url: imageUrl,
          }),
        }
      );

      const analysisResult = await analysisResp.json();

      if (analysisResult?.statusCode?.toString().startsWith("2")) {
        setImageAnalysis(analysisResult?.result);
        setPhotoAccepted(true);
      }

      // setCapturedImage(null);
      if (analysisResult?.result?.face_detected) {
        setFaceDetected(true);
        if (
          analysisResult.result.image_quality !== "good" ||
          analysisResult.result.lighting_quality !== "good" ||
          analysisResult.result.face_position !== "good"
        ) {
          setUploadError(
            `Image accepted but quality could be improved. Consider: ${
              analysisResult.result.image_quality !== "good"
                ? "better image clarity, "
                : ""
            }${
              analysisResult.result.lighting_quality !== "good"
                ? "better lighting, "
                : ""
            }${
              analysisResult.result.face_position !== "good"
                ? "more direct face position"
                : ""
            }`
          );
        }
      } else {
        const reason = analysisResult?.result?.reason_if_no_face;
        setFaceDetected(false);
        setUploadError(
          `Unable to properly analyze image: ${
            reason ||
            "No face detected or face not clearly visible. Please upload a clear, well-lit photo of your face looking directly at the camera."
          }`
        );
      }
    } catch (error) {
      console.error("Image processing error:", error);
      setUploadError("An error occurred during processing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const calculateFacialSymptomScore = (symptoms) => {
    // Define weights for each symptom
    const weights = {
      eye_redness: 1.5,
      eye_puffiness: 1.5,
      nasal_inflammation: 2.0,
      dark_circles: 2.0,
      facial_fatigue: 1.0,
    };

    const maxScore = 8.0; // Maximum possible weighted score
    let weightedSum = 0;
    let detectedSymptomCount = 0;

    for (const [symptom, isPresent] of Object.entries(symptoms)) {
      if (symptom in weights && isPresent === true) {
        weightedSum += weights[symptom];
        detectedSymptomCount++;
      }
    }

    let normalizedScore = (weightedSum / maxScore) * 10;

    // New Rule: If at least one symptom is detected, ensure score is at least Moderate (3.1)
    if (detectedSymptomCount > 0 && normalizedScore < 3.1) {
      normalizedScore = 3.1;
    }

    // Classify risk level based on the potentially adjusted score
    let riskLevel = "Low";
    if (normalizedScore > 6.0) riskLevel = "High";
    else if (normalizedScore >= 3.1) riskLevel = "Moderate"; // Use >= 3.1 for Moderate

    return {
      raw_score: weightedSum,
      normalized_score: Math.round(normalizedScore * 10) / 10, // Round to 1 decimal place
      risk_level: riskLevel,
    };
  };

  const normalizeEnvironmentalFactor = (value, min, max, optimal = null) => {
    if (value === null || value === undefined) return 0;

    // If there's an optimal range (like for humidity), use a different approach
    if (optimal && Array.isArray(optimal) && optimal.length === 2) {
      const [optMin, optMax] = optimal;
      if (value >= optMin && value <= optMax) {
        return 0; // Optimal range has zero risk
      } else if (value < optMin) {
        // Below optimal: normalize from min to optMin
        return Math.min(1, Math.max(0, 1 - (value - min) / (optMin - min)));
      } else {
        // Above optimal: normalize from optMax to max
        return Math.min(1, Math.max(0, (value - optMax) / (max - optMax)));
      }
    }

    // Standard min-max normalization
    return Math.min(1, Math.max(0, (value - min) / (max - min)));
  };
  const calculateEnvironmentalRiskScore = (envData) => {
    const factors = {
      aqi: { weight: 0.25, min: 0, max: 300, moderateThreshold: 51 }, // AQI > 50 is Moderate
      pm2_5: { weight: 0.15, min: 0, max: 150, moderateThreshold: 12.1 }, // PM2.5 > 12.0 is Moderate
      pollen_count_numeric: {
        weight: 0.25,
        min: 0,
        max: 1500,
        moderateThreshold: 301,
      }, // Assuming pollen "Moderate" starts ~300
      humidity: {
        weight: 0.1,
        min: 0,
        max: 100,
        optimal: [40, 60],
        moderateThresholdLow: 30,
        moderateThresholdHigh: 70,
      }, // Humidity <30 or >70 might be moderate risk
      temperature: {
        weight: 0.1,
        min: -10,
        max: 45,
        optimal: [10, 35],
        moderateThresholdLow: 5,
        moderateThresholdHigh: 30,
      }, // Temp <5 or >30 might be moderate
      // wind_speed: { weight: 0.05, min: 0, max: 20 }, // Not directly used for moderate count for simplicity here
      ozone: { weight: 0.1, min: 0, max: 100, moderateThreshold: 51 }, // Ozone (similar to AQI parts) > 50 could be moderate
    };

    const pollenCountMap = {
      Low: 100,
      Moderate: 500,
      High: 1000,
      "Very High": 1500,
      "N/A": 0,
    };

    const normalizedData = {
      aqi: normalizeEnvironmentalFactor(
        envData.aqi,
        factors.aqi.min,
        factors.aqi.max
      ),
      pm2_5: normalizeEnvironmentalFactor(
        envData.pm2_5,
        factors.pm2_5.min,
        factors.pm2_5.max
      ),
      pollen_count_numeric: normalizeEnvironmentalFactor(
        pollenCountMap[envData.pollen_count] || 0,
        factors.pollen_count_numeric.min,
        factors.pollen_count_numeric.max
      ),
      humidity: normalizeEnvironmentalFactor(
        envData.humidity,
        factors.humidity.min,
        factors.humidity.max,
        factors.humidity.optimal
      ),
      temperature: normalizeEnvironmentalFactor(
        envData.temperature,
        factors.temperature.min,
        factors.temperature.max,
        factors.temperature.optimal
      ),
      ozone: normalizeEnvironmentalFactor(
        envData.ozone || 0,
        factors.ozone.min,
        factors.ozone.max
      ), // Assume 0 if not provided
    };

    let weightedSum = 0;
    let totalWeight = 0;

    for (const [factor, factorInfo] of Object.entries(factors)) {
      if (normalizedData[factor] !== undefined && factorInfo.weight > 0) {
        // Only consider weighted factors
        weightedSum += normalizedData[factor] * factorInfo.weight;
        totalWeight += factorInfo.weight;
      }
    }

    let normalizedScore =
      totalWeight > 0 ? (weightedSum / totalWeight) * 10 : 0; // Initial score based on weights

    // Count parameters in "Moderate" or worse state
    let moderateOrWorseCount = 0;
    if (envData.aqi >= factors.aqi.moderateThreshold) moderateOrWorseCount++;
    if (envData.pm2_5 >= factors.pm2_5.moderateThreshold)
      moderateOrWorseCount++;
    const numericPollen = pollenCountMap[envData.pollen_count] || 0;
    if (
      numericPollen >= factors.pollen_count_numeric.moderateThreshold &&
      envData.pollen_count !== "Low" &&
      envData.pollen_count !== "N/A"
    )
      moderateOrWorseCount++; // Pollen "Moderate" or higher
    if (
      envData.humidity < factors.humidity.moderateThresholdLow ||
      envData.humidity > factors.humidity.moderateThresholdHigh
    )
      moderateOrWorseCount++;
    if (
      envData.temperature < factors.temperature.moderateThresholdLow ||
      envData.temperature > factors.temperature.moderateThresholdHigh
    )
      moderateOrWorseCount++;
    if ((envData.ozone || 0) >= factors.ozone.moderateThreshold)
      moderateOrWorseCount++;

    // New Rule: If 2 or more environmental parameters are moderate/worse, ensure score is at least Moderate
    if (moderateOrWorseCount >= 2 && normalizedScore < 3.1) {
      normalizedScore = 3.1;
    }

    let riskLevel = "Low";
    if (normalizedScore > 8.0) riskLevel = "Very High";
    else if (normalizedScore > 6.0) riskLevel = "High";
    else if (normalizedScore >= 3.1) riskLevel = "Moderate"; // Use >= 3.1 for Moderate

    return {
      normalized_score: Math.round(normalizedScore * 10) / 10,
      risk_level: riskLevel,
      factor_scores: normalizedData,
      moderate_or_worse_param_count: moderateOrWorseCount, // For debugging or potential display
    };
  };
  const calculateCombinedRiskScore = (facialScore, environmentalScore) => {
    // Simple weighted formula: 60% facial + 40% environmental
    const weightedScore = 0.6 * facialScore + 0.4 * environmentalScore;

    // Round to one decimal place
    const finalScore = Math.round(weightedScore * 10) / 10;

    // Determine risk level based on the weighted score
    let riskLevel = "Low";
    if (finalScore > 8.0) {
      riskLevel = "Very High";
    } else if (finalScore > 6.0) {
      riskLevel = "High";
    } else if (finalScore > 3.0) {
      riskLevel = "Moderate";
    }

    return {
      score: finalScore,
      risk_level: riskLevel,
      contributors: {
        facial: {
          score: facialScore,
          risk_level:
            facialScore > 6.0 ? "High" : facialScore > 3.0 ? "Moderate" : "Low",
        },
        environmental: {
          score: environmentalScore,
          risk_level:
            environmentalScore > 8.0
              ? "Very High"
              : environmentalScore > 6.0
              ? "High"
              : environmentalScore > 3.0
              ? "Moderate"
              : "Low",
        },
      },
    };
  };

  const processAssessment = async () => {
    if (!imageUrl || !userLocation) return;

    const headers = {
      "Content-Type": "application/json",
      Authorization: allergy_ai_api_secret_key,
    };

    try {
      setIsProcessing(true);

      // Face Allergy Detection

      const allergyDetectionResponse = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/allergy-assessment",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            action: "face_allergy_analysis",
            allergy_assessment_id: assesmentId,
            file_url: imageUrl,
            location: userLocation,
            notes: { app_code: "CPALLGY", client_code: state?.clientCode },
          }),
        }
      );
      const faceAllergyData = await allergyDetectionResponse.json();

      const {
        eye_redness = false,
        eye_puffiness = false,
        nasal_inflammation = false,
        dark_circles = false,
        facial_fatigue = false,
        detection_confidence = {},
      } = faceAllergyData.result || {};

      const finalSymptoms = {
        eye_redness,
        eye_puffiness,
        nasal_inflammation,
        dark_circles,
        facial_fatigue,
      };

      const facialScore = calculateFacialSymptomScore(finalSymptoms);
      const envData = routerLocation?.state.environmentalData || {};
      const environmentalScore = calculateEnvironmentalRiskScore(envData);

      //Combined Risk Calculation
      const combinedRisk = calculateCombinedRiskScore(
        facialScore.normalized_score,
        environmentalScore.normalized_score
      );

      //Allergy Prevention (Structured Recommendations)
      const preventionResponse = await fetch(
        "https://sdk-staging.carenow.healthcare/allergy/allergy-assessment",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            api_key: allergy_ai_api_key,
            employee_id: "9830374972",
            action: "allergy_prevention",
            allergy_assessment_id: assesmentId,
            location: userLocation,
            file_url: imageUrl,
          }),
        }
      );
      const preventionData = await preventionResponse.json();
      const structuredRecommendations =
        preventionData?.result?.recommendation || {};

      // Save final structured result
      const assessmentDataToSave = {
        date: new Date().toISOString(),
        image_url: imageUrl,
        location: userLocation,
        facial_score: facialScore.normalized_score * 10,
        environmental_score: environmentalScore.normalized_score * 10,
        overall_risk_score: Math.round(
          (facialScore.normalized_score * 0.6 +
            environmentalScore.normalized_score * 0.4) *
            10
        ),
        risk_level: combinedRisk.risk_level,
        symptoms_detected: finalSymptoms,
        environmental_data: envData,
        facial_risk_level: combinedRisk.contributors.facial.risk_level,
        environmental_risk_level:
          combinedRisk.contributors.environmental.risk_level,
        detailed_scores: {
          facial: combinedRisk.contributors.facial,
          environmental: combinedRisk.contributors.environmental,
          combined: combinedRisk,
        },
        // recommendation_summary: reviewSummary,
        recommendation: structuredRecommendations,
        image_analyis: imageAnalysis,
        confidence_data: imageAnalysis,
      };

      navigate("/assessment-result", { state: { data: assessmentDataToSave } });

      // if (isMountedRef.current) {
      //   setAssessmentResult(assessmentDataToSave);
      // }
    } catch (error) {
      console.error("Error during assessment:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <section className="pt-2 flex flex-col">
      <div className="px-6">
        <div className="flex items-center justify-between mb-8 w-full relative z-50 tab-screen lg:mb-8">
          <Link
            to={-1}
            className=" w-9 h-9 bg-[#2D2D2D] rounded-full flex flex-row justify-center items-center"
          >
            <img src={BackArrow} alt="" />
          </Link>
          <h2 className="text-xl lg:text-2xl md:text-2xl font-medium text-white">
            Allergy<span className="text-secondary">Ai</span>
          </h2>
          <img src={Clock} className="w-8 h-8" alt="" />
        </div>

        <div className="w-full relative tab-screen">
          <div className="circle-blur w-[70%] h-60 mx-auto absolute left-0 right-0 top-0 blur-[100px]"></div>
          <h1 className="text-base font-semibold text-white mb-4 relative z-50 text-center">
            New Assessment
          </h1>
          <div className="bg-[#1D1D1D]/70 rounded-[20px] px-6 pt-5 pb-7 mb-3 relative z-50">
            <div className="flex flex-col items-center">
              <div className="upload-image w-full">
                <div className="flex flex-col items-center justify-center">
                  <ImageUploading
                    multiple
                    value={images}
                    onChange={onChange}
                    maxNumber={maxNumber}
                    dataURLKey="data_url"
                    acceptType={["jpg", "jpeg", "png"]}
                  >
                    {({
                      imageList,
                      onImageUpload,
                      onImageRemoveAll,
                      onImageUpdate,
                      onImageRemove,
                      isDragging,
                      dragProps,
                    }) => (
                      // write your building UI
                      <div className="upload__image-wrapper w-full">
                        {imageSeleted && (
                          <div className="flex flex-col items-center justify-center">
                            <h4 className="text-base font-semibold text-secondary text-center mb-12">
                              Upload Facial Photo
                            </h4>
                            <img
                              src={NoImage}
                              className="w-10 h-10 mb-12"
                              alt=""
                            />

                            <p className="text-xs font-light text-white text-center mx-auto mb-6">
                              Select a clear photo of your face for
                              <br /> allergic rhinitis symptom analysis.
                            </p>
                            <button
                              className="w-36 h-8 bg-white rounded-full text-xs text-primary font-medium mx-auto"
                              style={isDragging ? { color: "red" } : null}
                              onClick={onImageUpload}
                              {...dragProps}
                            >
                              Choose Photo
                            </button>
                          </div>
                        )}

                        {imageList.map((image, index) => (
                          <div
                            key={index}
                            className="image-item w-full flex flex-col items-center justify-center"
                          >
                            <h4 className="text-base font-semibold text-secondary text-center mb-4">
                              Upload Facial Photo
                            </h4>
                            <div className="w-[150px] h-[174px] mx-auto overflow-hidden">
                              <img
                                src={image.data_url}
                                alt=""
                                className="w-auto h-full object-contain"
                              />
                            </div>
                            {imageAnalysis?.overall_confidence > 0 && (
                              <p className="text-xs font-light text-white my-3 text-center">
                                Photo selected.
                                <br /> Face detected with 
                                {imageAnalysis?.overall_confidence}% confidence.
                              </p>
                            )}

                            {uploadError && (
                              <div className="text-xxs text-error text-justify mt-2 leading-4 font-light">
                                <span>{uploadError}</span>
                              </div>
                            )}
                            <div className=" mt-5 flex flex-row items-center justify-between w-full">
                              <button
                                className="border border-white rounded-full w-[47%] h-9 flex flex-row justify-center items-center text-xs font-medium text-white"
                                onClick={() => {
                                  onImageRemove(index);
                                  setImageSeleceted(true);
                                  setPhotoAccepted(false);
                                  setUploadError("");
                                  setImageAnalysis({});
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                className="bg-white rounded-full w-[47%] h-9 flex flex-row justify-center items-center text-xs font-medium text-black disabled:bg-lightgray"
                                onClick={getConfidence}
                                disabled={photoAccepted || isLoading}
                              >
                                Accept Photo
                                {isLoading && (
                                  <Loader className="shrink-0 h-4 w-4 ml-2" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ImageUploading>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 px-8 pb-20 w-full bg-white responsive-white-card">
        <div className="tab-screen">
          <button
            onClick={processAssessment}
            disabled={
              isProcessing ||
              !imageUrl ||
              !userLocation ||
              !photoAccepted ||
              !isFaceDetected
            }
            className={`mt-8 w-9/12 mx-auto rounded-full outline-none px-5 h-12 bg-primary text-white flex flex-row justify-center items-center disabled:bg-darkgray`}
          >
            Take Assessment
            {isProcessing && <Loader className="shrink-0 h-4 w-4 ml-2" />}
          </button>
          <h5 className="text-sm font-semibold text-primary mt-5">
            Disclaimer
          </h5>
          <p className="text-xxs font-light text-primary mt-2 text-justify">
            AllergyAI is intended for informational and educational purposes
            only. The app’s Rhinitis Risk assessments are based on algorithmic
            analyses of sensor and environmental data and should not be used as
            a substitute for professional medical diagnosis, advice, or
            treatment. Results may vary based on individual factors, device
            conditions, and data quality. Always consult a qualified healthcare
            provider for personalized evaluation and before making any
            health-related decisions. CarePlix and its developers are not
            responsible for any adverse outcomes resulting from use of the app.
          </p>
        </div>
      </div>
      {/* <Link to={-1} className="block mt-4 text-sm font-medium  text-white">
      Back
    </Link> */}
    </section>
  );
};

export default NewAssessment;
